/**
 * Custom hook for checking feature access based on subscription.
 *
 * Provides convenient methods to check feature availability and plan requirements.
 */

import { useMemo } from 'react';
import { useSubscription } from './SubscriptionContext';
import { useAuth } from '../AuthContext';
import { PLAN_TIERS, PlanTier } from '../../types/subscription';

interface FeatureAccessHook {
  // Feature checks
  hasFeature: (feature: string) => boolean;
  hasUseCaseAccess: (useCase: string) => boolean;
  canCreateSimulation: (personas: number, segments: number) => boolean;
  canUpgrade: () => boolean;

  // Plan information
  currentPlan: string;
  isAdmin: boolean;
  isPaidPlan: boolean;
  isFreePlan: boolean;

  // Usage information
  isNearLimit: boolean;
  usagePercentage: number;
  remainingSimulations: number;

  // Upgrade suggestions
  getUpgradeReason: () => string | null;
  getRequiredPlanFor: (useCase: string) => string | null;

  // Loading states
  loading: boolean;
  error: string | null;
}

export const useFeatureAccess = (): FeatureAccessHook => {
  const {
    currentSubscription,
    featureAccess,
    usage,
    loading,
    error,
    hasFeature,
    isWithinLimits,
    canUpgrade,
    getUpgradeRecommendation,
  } = useSubscription();

  const { isAdmin: userIsAdmin } = useAuth();

  // Memoized computed values
  const computedValues = useMemo(() => {
    const currentPlan = currentSubscription?.id || PLAN_TIERS.FREE;
    const isAdmin = userIsAdmin || false;
    const isPaidPlan = currentPlan !== PLAN_TIERS.FREE;
    const isFreePlan = currentPlan === PLAN_TIERS.FREE;

    const usagePercentage = usage?.usagePercentage || 0;
    const remainingSimulations = usage?.remainingSimulations || 0;
    const isNearLimit = usagePercentage > 80;

    return {
      currentPlan,
      isAdmin,
      isPaidPlan,
      isFreePlan,
      usagePercentage,
      remainingSimulations,
      isNearLimit,
    };
  }, [currentSubscription, userIsAdmin, usage]);

  /**
   * Check if user has access to a specific use case
   */
  const hasUseCaseAccess = (useCase: string): boolean => {
    // Admin users have access to everything
    if (computedValues.isAdmin) return true;

    if (!featureAccess) return false;

    // Check if use case is in the allowed list
    const useCaseInfo = featureAccess.useCases[useCase];
    return useCaseInfo?.hasAccess || false;
  };

  /**
   * Check if user can create a simulation with given parameters
   */
  const canCreateSimulation = (personas: number, segments: number): boolean => {
    // Admin users bypass all limits
    if (computedValues.isAdmin) return true;

    // Check subscription limits
    if (!isWithinLimits(personas, segments)) return false;

    // Check usage limits
    if (usage && usage.simulationsLimit !== -1) {
      return usage.remainingSimulations > 0;
    }

    return true;
  };

  /**
   * Get upgrade reason based on current usage and limits
   */
  const getUpgradeReason = (): string | null => {
    if (computedValues.isAdmin) return null;

    // Check if approaching usage limits
    if (computedValues.isNearLimit) {
      return 'You are approaching your monthly simulation limit';
    }

    // Check if at usage limit
    if (usage && usage.remainingSimulations === 0) {
      return 'You have reached your monthly simulation limit';
    }

    // Check if on free plan with basic features
    if (computedValues.isFreePlan) {
      return 'Upgrade to access more use cases and increase your limits';
    }

    // Check if could benefit from higher tier features
    const recommendation = getUpgradeRecommendation();
    if (recommendation) {
      return `Consider upgrading to ${recommendation} for better features`;
    }

    return null;
  };

  /**
   * Get the minimum required plan for a specific use case
   */
  const getRequiredPlanFor = (useCase: string): string | null => {
    if (!featureAccess) return null;

    const useCaseInfo = featureAccess.useCases[useCase];
    return useCaseInfo?.minPlanRequired || null;
  };

  /**
   * Check if a plan upgrade would unlock a specific feature
   */
  const wouldPlanUnlock = (targetPlan: PlanTier, feature: string): boolean => {
    const planHierarchy = [
      PLAN_TIERS.FREE,
      PLAN_TIERS.STARTER,
      PLAN_TIERS.PROFESSIONAL,
      PLAN_TIERS.BUSINESS,
      PLAN_TIERS.ENTERPRISE,
    ];

    const currentIndex = planHierarchy.indexOf(computedValues.currentPlan as PlanTier);
    const targetIndex = planHierarchy.indexOf(targetPlan);

    // Feature availability by plan (this could be moved to configuration)
    const featureAvailability: Record<string, PlanTier[]> = {
      priority_support: [PLAN_TIERS.PROFESSIONAL, PLAN_TIERS.BUSINESS, PLAN_TIERS.ENTERPRISE],
      api_access: [PLAN_TIERS.BUSINESS, PLAN_TIERS.ENTERPRISE],
      custom_branding: [PLAN_TIERS.ENTERPRISE],
      dedicated_account_manager: [PLAN_TIERS.ENTERPRISE],
    };

    const availableInPlans = featureAvailability[feature] || [];

    return targetIndex > currentIndex && availableInPlans.includes(targetPlan);
  };

  /**
   * Get benefits of upgrading to a specific plan
   */
  const getUpgradeBenefits = (targetPlan: PlanTier): string[] => {
    const benefits: string[] = [];

    // Check which features would be unlocked
    if (wouldPlanUnlock(targetPlan, 'priority_support')) {
      benefits.push('Priority customer support');
    }

    if (wouldPlanUnlock(targetPlan, 'api_access')) {
      benefits.push('API access for integrations');
    }

    if (wouldPlanUnlock(targetPlan, 'custom_branding')) {
      benefits.push('Custom branding options');
    }

    if (wouldPlanUnlock(targetPlan, 'dedicated_account_manager')) {
      benefits.push('Dedicated account manager');
    }

    // Add usage-related benefits
    switch (targetPlan) {
      case PLAN_TIERS.STARTER:
        benefits.push('50 simulations per month', 'Access to AB Testing');
        break;
      case PLAN_TIERS.PROFESSIONAL:
        benefits.push('200 simulations per month', 'Advanced use cases', 'Campaign strategy tools');
        break;
      case PLAN_TIERS.BUSINESS:
        benefits.push(
          '500 simulations per month',
          'All creative tools',
          'Amazon marketplace testing',
        );
        break;
      case PLAN_TIERS.ENTERPRISE:
        benefits.push('Unlimited simulations', 'All features included', 'White-label options');
        break;
    }

    return benefits;
  };

  return {
    // Feature checks
    hasFeature,
    hasUseCaseAccess,
    canCreateSimulation,
    canUpgrade,

    // Plan information
    currentPlan: computedValues.currentPlan,
    isAdmin: computedValues.isAdmin,
    isPaidPlan: computedValues.isPaidPlan,
    isFreePlan: computedValues.isFreePlan,

    // Usage information
    isNearLimit: computedValues.isNearLimit,
    usagePercentage: computedValues.usagePercentage,
    remainingSimulations: computedValues.remainingSimulations,

    // Upgrade suggestions
    getUpgradeReason,
    getRequiredPlanFor,

    // Loading states
    loading,
    error,

    // Additional utilities (not in interface but available)
    wouldPlanUnlock,
    getUpgradeBenefits,
  } as FeatureAccessHook & {
    wouldPlanUnlock: typeof wouldPlanUnlock;
    getUpgradeBenefits: typeof getUpgradeBenefits;
  };
};

export default useFeatureAccess;
